import { EditOutlined } from "@ant-design/icons";
import { Form, InputNumber, message, Modal, Tooltip, Typography } from "antd";
import { useForm } from "antd/es/form/Form";
import { Button } from "antd/lib";
import { useUpdateCustomerPackageCredits } from "hooks/mutations/customerpackage";
import { useState } from "react";
import { translate } from "shared/languages";
import { CustomerPackage } from "shared/types";

interface UpdateCustomerPackageCreditsModalProps {
  customerPackage: CustomerPackage;
  disabled?: boolean;
  /**
   * Pembatasan Admin: disabled with NO popover at all. A Manager's
   * restriction is deliberately silent on hover (Andrew, 2026-09-03).
   */
  restricted?: boolean;
}

const UpdateCustomerPackageCreditsModal = ({ customerPackage, disabled = false, restricted = false }: UpdateCustomerPackageCreditsModalProps) => {
  const [form] = useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { mutate: updateCustomerPackageCredits } = useUpdateCustomerPackageCredits();

  // Remaining credits stay between the on-hold count (upcoming bookings keep
  // their reserved credit) and the package's original total.
  const minCredits = Math.max(customerPackage.onhold_credits ?? 0, 0);
  const maxCredits = customerPackage.total_credits;

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        updateCustomerPackageCredits({
          packageId: customerPackage.id,
          remaining_credits: values.remaining_credits,
        });
        setIsModalOpen(false);
      })
      .catch(() => {
        message.error(translate("messages.error.invalid_form"));
        setIsModalOpen(false);
      });
  };

  return (
    <>
      {restricted ? (
        <Button color="primary" type="link" icon={<EditOutlined />} disabled />
      ) : (
        <Tooltip title={disabled ? translate("popover.edit_remaining_credits_fail") : translate("popover.edit_remaining_credits")}>
          <Button color="primary" type="link" icon={<EditOutlined />} onClick={() => setIsModalOpen(true)} disabled={disabled} />
        </Tooltip>
      )}
      <Modal
        title={translate("title.update_remaining_credits")}
        open={isModalOpen}
        onOk={onSubmit}
        onCancel={() => setIsModalOpen(false)}
        okText={translate("button.confirm")}
        afterClose={() => form.resetFields()}
      >
        <Form form={form} layout="vertical" name="updateRemainingCredits">
          <Form.Item
            name="remaining_credits"
            label={translate("label.remaining_session")}
            initialValue={customerPackage.remaining_credits}
            rules={[
              {
                required: true,
                type: "number",
                min: minCredits,
                max: maxCredits,
                message: `${translate("form.error.input")} ${translate("label.remaining_session")}`,
              },
            ]}
          >
            <InputNumber
              style={{ width: "100%" }}
              min={minCredits}
              max={maxCredits}
              placeholder={`${translate("form.placeholders.enter")} ${translate("label.remaining_session")}`}
            />
          </Form.Item>
          {/* Only worth explaining when there's actually something to explain —
              an always-shown "2 = 2 + 0" reads as clutter (and debug-y) in the
              common case where nothing is on hold; this only appears when it's
              genuinely relevant, and states the reason together with the
              consequence (the field's own min) in one sentence. */}
          {minCredits > 0 && (
            <Typography.Text type="secondary" style={{ display: "block" }}>
              {minCredits} {translate("messages.info.remaining_credits_min_onhold")}
            </Typography.Text>
          )}
          {/* The one rule an admin can trip without noticing: a balance above
              total − used is accepted, and the extra classes earn Rp 0. */}
          <Typography.Text type="secondary" style={{ display: "block", marginTop: 8 }}>
            {translate("messages.info.credits_goodwill_hint")}
          </Typography.Text>
        </Form>
      </Modal>
    </>
  );
};

export default UpdateCustomerPackageCreditsModal;
